import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchPropertiesREST } from '../services/propertiesREST';
import { notifyAdminsOfNewLead } from '../services/emailNotifier';

/**
 * PropertyContext — Zero Firebase SDK dependency at module level.
 * 
 * PUBLIC visitors: Properties are fetched via Firestore REST API (native fetch).
 *   → No WebChannel, no /Listen/channel, no Firebase SDK in the critical path.
 * 
 * ADMIN writes: Firebase SDK is lazy-imported on first write operation.
 *   → createProperty, updateProperty, deleteProperty, incrementPropertyMetric
 *   → createLegalRequest, deleteLegalRequest
 *   → createClientDoc, updateClientDoc, deleteClientDoc
 */

const PropertyContext = createContext();

const STORAGE_KEYS = {
  PROPERTIES: 'em_asesorias_properties_v3',
  LEADS: 'em_asesorias_leads_v3',
  CLIENTS: 'em_asesorias_clients_v3'
};

// ─── Lazy Service Loaders ───────────────────────────────────────────────
// Firebase SDK modules are loaded ONLY on first admin action (write/subscribe).
let _propertiesService = null;
let _leadsService = null;
let _clientsService = null;

const getPropertiesService = async () => {
  if (!_propertiesService) {
    _propertiesService = await import('../firebase/propertiesService');
  }
  return _propertiesService;
};

const getLeadsService = async () => {
  if (!_leadsService) {
    _leadsService = await import('../firebase/leadsService');
  }
  return _leadsService;
};

const getClientsService = async () => {
  if (!_clientsService) {
    _clientsService = await import('../firebase/clientsService');
  }
  return _clientsService;
};

export const PropertyProvider = ({ children }) => {
  // Properties State (Populated strictly from Firestore)
  const [properties, setProperties] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.PROPERTIES);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Error loading properties from storage', e);
    }
    return [];
  });

  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [isFirestoreConnected, setIsFirestoreConnected] = useState(false);

  // Global Filter State
  const [filters, setFilters] = useState({
    type: 'Todos los tipos',
    operation: 'Todos',
    locationQuery: '',
    onlyDiscounted: false,
    minPrice: 0,
    maxPrice: 20000000,
    bedrooms: 'Cualquiera',
    bathrooms: 'Cualquiera',
    searchQuery: ''
  });

  // View Mode: 'grid' or 'map'
  const [viewMode, setViewMode] = useState('grid');
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  // Sync with Firestore via REST API (no Firebase SDK, no WebChannel, no /Listen/channel)
  useEffect(() => {
    let isMounted = true;

    const loadProperties = async () => {
      try {
        const firestoreProps = await fetchPropertiesREST();
        if (!isMounted) return;
        if (firestoreProps && firestoreProps.length > 0) {
          setProperties(firestoreProps);
          setIsFirestoreConnected(true);
          try {
            const compactCache = firestoreProps.map(p => ({
              ...p,
              images: Array.isArray(p.images) ? p.images.slice(0, 2) : []
            }));
            localStorage.setItem(STORAGE_KEYS.PROPERTIES, JSON.stringify(compactCache));
          } catch (e) {
            // Quota handled
          }
        }
      } catch (error) {
        console.warn('Usando base local/cacheada de propiedades:', error?.message || error);
      }
    };

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, []);

  // CRUD Actions for Properties (lazy-load Firebase SDK on demand)
  const addProperty = async (newProperty) => {
    try {
      const { createProperty } = await getPropertiesService();
      const savedDoc = await createProperty(newProperty);
      setProperties(prev => [savedDoc, ...prev.filter(p => p.id !== savedDoc.id)]);
      return savedDoc;
    } catch (error) {
      console.error('Error al guardar propiedad en Firestore:', error);
      const propertyWithId = {
        ...newProperty,
        id: `EM-${Math.floor(100 + Math.random() * 900)}`,
        slug: (newProperty.title || 'propiedad').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        viewsCount: 0,
        whatsappClicks: 0
      };
      setProperties(prev => [propertyWithId, ...prev]);
      return propertyWithId;
    }
  };

  const updateProperty = async (id, updatedFields) => {
    try {
      const { updatePropertyDoc } = await getPropertiesService();
      await updatePropertyDoc(id, updatedFields);
      setProperties(prev =>
        prev.map(prop => {
          if (prop.id === id) {
            return {
              ...prop,
              ...updatedFields,
              // Explicitly preserve existing analytics & views counters
              analytics: {
                views: prop.analytics?.views ?? prop.viewsCount ?? 0,
                whatsappClicks: prop.analytics?.whatsappClicks ?? prop.whatsappClicks ?? 0,
                leadsCount: prop.analytics?.leadsCount ?? 0,
                ...(updatedFields.analytics || {})
              },
              viewsCount: prop.viewsCount ?? prop.analytics?.views ?? 0,
              whatsappClicks: prop.whatsappClicks ?? prop.analytics?.whatsappClicks ?? 0
            };
          }
          return prop;
        })
      );
    } catch (error) {
      console.error('Error al actualizar propiedad:', error);
      setProperties(prev =>
        prev.map(prop => (prop.id === id ? { ...prop, ...updatedFields } : prop))
      );
    }
  };

  const deleteProperty = async (id) => {
    try {
      const { deletePropertyDoc } = await getPropertiesService();
      await deletePropertyDoc(id);
      setProperties(prev => prev.filter(prop => prop.id !== id));
    } catch (error) {
      console.error('Error al eliminar propiedad en Firestore:', error);
      setProperties(prev => prev.filter(prop => prop.id !== id));
    }
  };

  const toggleStatus = async (id, newStatus) => {
    const statusVal = newStatus.toLowerCase();
    await updateProperty(id, { status: statusVal });
  };

  const toggleFeatured = async (id) => {
    const current = properties.find(p => p.id === id);
    if (current) {
      await updateProperty(id, { featured: !current.featured });
    }
  };

  // Analytics Increment per Property (lazy-load Firebase SDK)
  const recordView = (id) => {
    if (!id) return;
    // Fire-and-forget: lazy-load SDK and increment
    getPropertiesService().then(({ incrementPropertyMetric }) => {
      incrementPropertyMetric(id, 'views');
    }).catch(() => {});
    setProperties(prev =>
      prev.map(prop => {
        if (prop.id === id) {
          const newViews = (Number(prop.analytics?.views || prop.viewsCount || 0)) + 1;
          return {
            ...prop,
            viewsCount: newViews,
            analytics: { ...(prop.analytics || {}), views: newViews }
          };
        }
        return prop;
      })
    );
  };

  const recordWhatsAppClick = (id) => {
    if (!id) return;
    getPropertiesService().then(({ incrementPropertyMetric }) => {
      incrementPropertyMetric(id, 'whatsappClicks');
    }).catch(() => {});
    setProperties(prev =>
      prev.map(prop => {
        if (prop.id === id) {
          const newClicks = (Number(prop.analytics?.whatsappClicks || prop.whatsappClicks || 0)) + 1;
          return {
            ...prop,
            whatsappClicks: newClicks,
            analytics: { ...(prop.analytics || {}), whatsappClicks: newClicks }
          };
        }
        return prop;
      })
    );
  };

  // Lead Registration (lazy-load Firebase SDK)
  const addLead = async (leadData) => {
    try {
      const { createLegalRequest } = await getLeadsService();
      const savedLead = await createLegalRequest({
        serviceType: leadData.serviceType || leadData.type || 'visita',
        category: leadData.category || (leadData.propertyId ? 'interes' : 'captacion'),
        clientName: leadData.name || leadData.clientName || '',
        phone: leadData.phone || '',
        email: leadData.email || '',
        message: leadData.message || '',
        propertyId: leadData.propertyId || null,
        propertyTitle: leadData.propertyTitle || null
      });

      if (leadData.propertyId) {
        getPropertiesService().then(({ incrementPropertyMetric }) => {
          incrementPropertyMetric(leadData.propertyId, 'leadsCount');
        }).catch(() => {});
        setProperties(prev =>
          prev.map(prop => {
            if (prop.id === leadData.propertyId) {
              const newCount = (Number(prop.analytics?.leadsCount || 0)) + 1;
              return {
                ...prop,
                analytics: { ...(prop.analytics || {}), leadsCount: newCount }
              };
            }
            return prop;
          })
        );
      }

      // Automatically dispatch email notification to administrators
      notifyAdminsOfNewLead(leadData);

      return savedLead;
    } catch (error) {
      console.error('Error guardando lead en Firestore:', error);
      const fallbackLead = {
        ...leadData,
        id: `LEAD-${Math.floor(100 + Math.random() * 900)}`,
        status: 'unread',
        createdAt: { seconds: Math.floor(Date.now() / 1000) }
      };
      setLeads(prev => [fallbackLead, ...prev]);

      // Attempt email dispatch even in fallback
      notifyAdminsOfNewLead(leadData);

      return fallbackLead;
    }
  };

  // Convert Lead to Client & Delete Lead (lazy-load Firebase SDK)
  const convertLeadToClient = async (leadId, clientData) => {
    try {
      const { createClientDoc } = await getClientsService();
      // 1. Create client document
      const newClient = await createClientDoc({
        name: clientData.name || clientData.clientName || '',
        phone: clientData.phone || '',
        email: clientData.email || '',
        origin: clientData.origin || clientData.serviceType || clientData.type || 'Contacto General',
        propertyId: clientData.propertyId || null,
        propertyTitle: clientData.propertyTitle || null,
        notes: clientData.notes || clientData.message || ''
      });

      // 2. Delete lead document from pending requests
      if (leadId) {
        const { deleteLegalRequest } = await getLeadsService();
        await deleteLegalRequest(leadId);
        setLeads(prev => prev.filter(l => l.id !== leadId));
      }

      setClients(prev => [newClient, ...prev.filter(c => c.id !== newClient.id)]);
      return newClient;
    } catch (error) {
      console.error('Error convirtiendo lead a cliente:', error);
      throw error;
    }
  };

  // Direct Client Management (lazy-load Firebase SDK)
  const addClient = async (clientData) => {
    try {
      const { createClientDoc } = await getClientsService();
      const newClient = await createClientDoc(clientData);
      setClients(prev => [newClient, ...prev.filter(c => c.id !== newClient.id)]);
      return newClient;
    } catch (error) {
      console.error('Error creando cliente:', error);
      throw error;
    }
  };

  const updateClient = async (clientId, updatedFields) => {
    try {
      const { updateClientDoc } = await getClientsService();
      await updateClientDoc(clientId, updatedFields);
      setClients(prev => prev.map(c => (c.id === clientId ? { ...c, ...updatedFields } : c)));
      return true;
    } catch (error) {
      console.error('Error actualizando cliente:', error);
      throw error;
    }
  };

  const updateClientNote = async (clientId, notes) => {
    try {
      const { updateClientDoc } = await getClientsService();
      await updateClientDoc(clientId, { notes });
      setClients(prev => prev.map(c => (c.id === clientId ? { ...c, notes } : c)));
    } catch (error) {
      console.error('Error actualizando nota del cliente:', error);
    }
  };

  const removeClient = async (clientId) => {
    try {
      const { deleteClientDoc } = await getClientsService();
      await deleteClientDoc(clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
    } catch (error) {
      console.error('Error eliminando cliente:', error);
    }
  };

  const removeLead = async (leadId) => {
    try {
      const { deleteLegalRequest } = await getLeadsService();
      await deleteLegalRequest(leadId);
      setLeads(prev => prev.filter(l => l.id !== leadId));
    } catch (error) {
      console.error('Error eliminando lead:', error);
    }
  };

  // Reset Filters
  const resetFilters = () => {
    setFilters({
      type: 'Todos los tipos',
      operation: 'Todos',
      locationQuery: '',
      onlyDiscounted: false,
      minPrice: 0,
      maxPrice: 20000000,
      bedrooms: 'Cualquiera',
      bathrooms: 'Cualquiera',
      searchQuery: ''
    });
  };

  // Filter Computation
  const filteredProperties = properties.filter(prop => {
    // 1. Tipo de Inmueble
    const type = (prop.propertyType || prop.type || '').toLowerCase();
    const filterType = filters.type.toLowerCase();
    if (filters.type !== 'Todos los tipos' && type !== filterType) {
      return false;
    }

    // 2. Compra / Renta (Operación)
    const op = (prop.operationType || prop.operation || '').toLowerCase();
    const filterOp = filters.operation.toLowerCase();
    if (filters.operation !== 'Todos' && op !== filterOp) {
      return false;
    }

    // 3. Ubicación (Colonia, Ciudad, Código Postal)
    if (filters.locationQuery && filters.locationQuery.trim() !== '') {
      const locQ = filters.locationQuery.toLowerCase().trim();
      const colonia = (prop.location?.neighborhood || prop.location?.colonia || '').toLowerCase();
      const city = (prop.location?.city || '').toLowerCase();
      const cp = (prop.location?.postalCode || prop.location?.zip || '').toLowerCase();
      if (!colonia.includes(locQ) && !city.includes(locQ) && !cp.includes(locQ)) {
        return false;
      }
    }

    // 4. Si tiene descuento en el precio
    if (filters.onlyDiscounted) {
      const effPrice = Number(prop.priceMXN || prop.price || 0);
      const origPrice = Number(prop.originalPriceMXN || prop.originalPrice || 0);
      const isDisc = Boolean(prop.hasDiscount || (origPrice > 0 && origPrice > effPrice));
      if (!isDisc) {
        return false;
      }
    }

    // 5. Precio
    const price = Number(prop.priceMXN || prop.price || 0);
    if (price < filters.minPrice || price > filters.maxPrice) {
      return false;
    }

    const beds = Number(prop.features?.bedrooms || prop.specs?.bedrooms || 0);
    if (filters.bedrooms !== 'Cualquiera') {
      const numBed = parseInt(filters.bedrooms, 10);
      if (beds < numBed) return false;
    }

    const baths = Number(prop.features?.fullBathrooms || prop.features?.bathrooms || prop.specs?.bathrooms || 0);
    if (filters.bathrooms !== 'Cualquiera') {
      const numBath = parseFloat(filters.bathrooms);
      if (baths < numBath) return false;
    }

    if (filters.searchQuery.trim() !== '') {
      const q = filters.searchQuery.toLowerCase();
      const matchTitle = (prop.title || '').toLowerCase().includes(q);
      const matchColonia = (prop.location?.neighborhood || prop.location?.colonia || '').toLowerCase().includes(q);
      const matchCity = (prop.location?.city || '').toLowerCase().includes(q);
      const matchId = (prop.id || '').toLowerCase().includes(q);
      const matchDesc = (prop.description || '').toLowerCase().includes(q);
      if (!matchTitle && !matchColonia && !matchCity && !matchId && !matchDesc) {
        return false;
      }
    }
    return true;
  });

  // Categorized Leads
  const captacionesLeads = leads.filter(l => l.category === 'captacion' || (!l.propertyId && l.serviceType?.includes('Propietario')));
  const propertyInterestLeads = leads.filter(l => l.category === 'interes' || Boolean(l.propertyId));

  // Global Aggregate Metrics
  const totalViews = properties.reduce((sum, p) => sum + Number(p.analytics?.views || p.viewsCount || 0), 0);
  const totalWhatsAppClicks = properties.reduce((sum, p) => sum + Number(p.analytics?.whatsappClicks || p.whatsappClicks || 0), 0);
  const totalLeads = leads.length;
  const activePropertiesCount = properties.filter(p => (p.status || '').toLowerCase() === 'disponible').length;

  // WhatsApp Link Generator
  const getWhatsAppLink = (customMessage = 'Hola Lic. Edgar Molina, deseo información sobre los servicios inmobiliarios y jurídicos de EM Asesoría.') => {
    const phone = '526272792964';
    return `https://wa.me/${phone}?text=${encodeURIComponent(customMessage)}`;
  };

  return (
    <PropertyContext.Provider
      value={{
        properties,
        filteredProperties,
        leads,
        captacionesLeads,
        propertyInterestLeads,
        clients,
        filters,
        setFilters,
        resetFilters,
        viewMode,
        setViewMode,
        selectedPropertyId,
        setSelectedPropertyId,
        addProperty,
        updateProperty,
        deleteProperty,
        toggleStatus,
        toggleFeatured,
        recordView,
        recordWhatsAppClick,
        addLead,
        removeLead,
        convertLeadToClient,
        addClient,
        updateClient,
        updateClientNote,
        removeClient,
        totalViews,
        totalWhatsAppClicks,
        totalLeads,
        activePropertiesCount,
        getWhatsAppLink,
        isFirestoreConnected
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperties = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperties must be used within a PropertyProvider');
  }
  return context;
};
